import { LinkOutlined, SwapOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { Button, Dropdown, message, Select, Space, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import {
  listEnabledRegionsForSwitch,
  type RegionItem,
} from '@/pages/miniprogram/regions/service';
import {
  getCompanyMiniPrograms,
  getMiniPrograms,
  type MiniProgramItem,
} from '@/pages/platform/service';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import {
  getAdminMiniProgramContext,
  setAdminMiniProgramContext,
} from '@/utils/adminMiniProgram';
import {
  getAdminRegionContext,
  setAdminRegionContext,
} from '@/utils/adminRegionContext';
import {
  type AdminView,
  getAdminView,
  inferAdminViewFromPath,
  setAdminView,
} from '@/utils/adminView';
import {
  resolveAdminViewSwitchPolicy,
  resolveAvailableAdminViews,
  resolveRegionSwitchMode,
} from '@/utils/adminViews';
import {
  buildFallbackCurrentUserFromToken,
  clearAdminToken,
  getAdminFallbackUser,
  getAdminToken,
  setAdminFallbackUser,
} from '@/utils/auth';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development' || process.env.CI;
const loginPath = '/user/login';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  adminView?: AdminView;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      if (msg.status === 1 && msg.result_body) {
        setAdminFallbackUser(undefined);
        return msg.result_body;
      }
      clearAdminToken();
      history.push(loginPath);
    } catch (_error) {
      const token = getAdminToken();
      const fallbackUser =
        getAdminFallbackUser() || buildFallbackCurrentUserFromToken(token);
      if (token && fallbackUser) {
        setAdminFallbackUser(fallbackUser);
        return fallbackUser;
      }
      clearAdminToken();
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (
    ![loginPath, '/user/register', '/user/register-result'].includes(
      location.pathname,
    )
  ) {
    const currentUser = await fetchUserInfo();
    const availableViews = resolveAvailableAdminViews(currentUser);
    const inferredView = inferAdminViewFromPath(location.pathname);
    const storedView = getAdminView();
    const adminView =
      (inferredView && availableViews.includes(inferredView)
        ? inferredView
        : undefined) ||
      (availableViews.includes(storedView) ? storedView : undefined) ||
      availableViews[0] ||
      'company';
    if (adminView === 'company') {
      await ensureDefaultMiniProgramContext(currentUser, location.pathname);
    }
    setAdminView(adminView);
    if (location.pathname === '/' && adminView === 'region') {
      history.replace('/region/miniprogram/devices');
    }
    return {
      fetchUserInfo,
      currentUser,
      adminView,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    adminView: 'company',
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

const isPlatformAdmin = (currentUser?: API.CurrentUser) => {
  const scope = currentUser?.adminScope || currentUser?.admin_scope;
  return (
    currentUser?.access === 'admin' &&
    (resolveAvailableAdminViews(currentUser).includes('platform') ||
      scope === 'platform')
  );
};

const getCurrentUserCompanyId = (currentUser?: API.CurrentUser) =>
  currentUser?.companyId ||
  (currentUser?.company_id === undefined
    ? undefined
    : String(currentUser.company_id));

const saveMiniProgramContext = (miniProgram: MiniProgramItem) => {
  setAdminMiniProgramContext({
    id: miniProgram.id,
    key: miniProgram.key,
    name: miniProgram.name,
    appId: miniProgram.appId,
    companyId: miniProgram.companyId,
    companyName: miniProgram.companyName,
    companyKey: miniProgram.companyKey,
    companyIsPlatformOwner: miniProgram.companyIsPlatformOwner,
    deviceLimit: miniProgram.deviceLimit ?? -1,
  });
};

const loadMiniProgramsForCurrentView = async (
  currentUser: API.CurrentUser | undefined,
  platformAdmin: boolean,
) => {
  if (platformAdmin) {
    const companyId =
      getAdminMiniProgramContext()?.companyId ||
      getCurrentUserCompanyId(currentUser);
    if (!companyId) {
      return [];
    }
    const result = await getMiniPrograms({
      current: 1,
      pageSize: 100,
      companyId,
    });
    return result.data;
  }

  return getCompanyMiniPrograms();
};

const ensureDefaultMiniProgramContext = async (
  currentUser: API.CurrentUser | undefined,
  pathname: string,
) => {
  if (!currentUser || !pathname.startsWith('/company')) {
    return;
  }

  const platformAdmin = isPlatformAdmin(currentUser);
  const companyId = getCurrentUserCompanyId(currentUser);
  const context = getAdminMiniProgramContext();

  if (
    !platformAdmin &&
    context?.companyId &&
    companyId &&
    context.companyId !== companyId
  ) {
    setAdminMiniProgramContext(undefined);
    return;
  }

  if (context?.id) {
    return;
  }

  const list = await loadMiniProgramsForCurrentView(currentUser, platformAdmin);
  const first = list[0];
  if (first) {
    saveMiniProgramContext(first);
  }
};

const shouldShowInView = (path: string | undefined, view: AdminView) => {
  if (!path) {
    return true;
  }
  if (view === 'platform') {
    return (
      path.startsWith('/platform') ||
      path.startsWith('/emqx') ||
      path.startsWith('/system') ||
      path.startsWith('/examples')
    );
  }
  if (view === 'region') {
    return path.startsWith('/region');
  }
  return path.startsWith('/company');
};

const filterMenuByView = (menuData: any[], view: AdminView): any[] =>
  menuData
    .map((item) => {
      const children = item.children
        ? filterMenuByView(item.children, view)
        : undefined;
      const visible = shouldShowInView(item.path, view);
      if (!visible && (!children || children.length === 0)) {
        return undefined;
      }
      return {
        ...item,
        children,
      };
    })
    .filter(Boolean);

const platformHomePath = '/platform/mini-programs';

const saveRegionContext = (region: RegionItem) => {
  setAdminRegionContext({
    id: region.id,
    name: region.name,
    companyId: region.companyId,
    miniProgramId: region.miniProgramId,
    miniProgramName: region.miniProgramName,
  });
};

const enterRegionView = (region: RegionItem) => {
  saveRegionContext(region);
  setAdminView('region');
  history.push('/region/miniprogram/devices');
};

const switchCurrentRegion = (region: RegionItem) => {
  saveRegionContext(region);
  history.go(0);
};

const RegionEntryAction: React.FC<{
  loading: boolean;
  regions: RegionItem[];
  canSelectAnyRegion: boolean;
}> = ({ loading, regions, canSelectAnyRegion }) => {
  const mode = resolveRegionSwitchMode(canSelectAnyRegion, regions.length);
  if (mode === 'hidden') {
    return null;
  }
  if (loading) {
    return (
      <Button loading icon={<SwapOutlined />}>
        切换至区域管理
      </Button>
    );
  }
  if (mode === 'disabled') {
    return (
      <Tooltip title="当前小程序暂无启用区域">
        <span>
          <Button disabled icon={<SwapOutlined />}>
            切换至区域管理
          </Button>
        </span>
      </Tooltip>
    );
  }
  if (mode === 'direct') {
    return (
      <Button
        icon={<SwapOutlined />}
        onClick={() => enterRegionView(regions[0])}
      >
        切换至区域管理
      </Button>
    );
  }
  return (
    <Dropdown
      menu={{
        items: regions.map((region) => ({
          key: region.id,
          label: region.name,
          onClick: () => enterRegionView(region),
        })),
      }}
    >
      <Button icon={<SwapOutlined />}>切换至区域管理</Button>
    </Dropdown>
  );
};

const MiniProgramContextSelector: React.FC<{
  platformAdmin: boolean;
  currentUser?: API.CurrentUser;
}> = ({ platformAdmin, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [items, setItems] = useState<MiniProgramItem[]>([]);
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const [value, setValue] = useState<string | undefined>(
    getAdminMiniProgramContext()?.id,
  );
  const switchPolicy = resolveAdminViewSwitchPolicy(currentUser, 'company');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadMiniProgramsForCurrentView(currentUser, platformAdmin)
      .then((list) => {
        if (!mounted) {
          return;
        }
        setItems(list);
        const current = getAdminMiniProgramContext();
        const next = list.find((item) => item.id === current?.id) || list[0];
        if (next) {
          saveMiniProgramContext(next);
          setValue(next.id);
        }
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });
    return () => {
      mounted = false;
    };
  }, [currentUser, platformAdmin]);

  useEffect(() => {
    let mounted = true;
    if (!value || !switchPolicy.canSelectAnyRegion) {
      setRegions([]);
      return () => {
        mounted = false;
      };
    }

    setRegionsLoading(true);
    listEnabledRegionsForSwitch()
      .then((list) => {
        if (mounted) {
          setRegions(list);
        }
      })
      .catch(() => {
        if (mounted) {
          setRegions([]);
          message.error('区域列表加载失败，请稍后重试');
        }
      })
      .finally(() => {
        if (mounted) {
          setRegionsLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [switchPolicy.canSelectAnyRegion, value]);

  return (
    <Space size={8}>
      <Select
        loading={loading}
        value={value}
        placeholder="选择小程序"
        style={{ minWidth: 240 }}
        optionLabelProp="label"
        options={items.map((item) => ({
          value: item.id,
          label: item.name || item.appId,
          item,
        }))}
        onChange={(_, option) => {
          const selected = (Array.isArray(option) ? option[0] : option)?.item as
            | MiniProgramItem
            | undefined;
          if (!selected) {
            return;
          }
          saveMiniProgramContext(selected);
          setAdminRegionContext(undefined);
          setValue(selected.id);
          history.go(0);
        }}
      />
      <RegionEntryAction
        loading={regionsLoading}
        regions={regions}
        canSelectAnyRegion={switchPolicy.canSelectAnyRegion}
      />
      {switchPolicy.showPlatformExit ? (
        <Button
          icon={<SwapOutlined />}
          onClick={() => {
            setAdminMiniProgramContext(undefined);
            setAdminRegionContext(undefined);
            setAdminView('platform');
            history.push(platformHomePath);
          }}
        >
          切换至平台管理
        </Button>
      ) : null}
    </Space>
  );
};

const RegionViewActions: React.FC<{
  currentUser?: API.CurrentUser;
}> = ({ currentUser }) => {
  const regionContext = getAdminRegionContext();
  const miniProgramContext = getAdminMiniProgramContext();
  const switchPolicy = resolveAdminViewSwitchPolicy(currentUser, 'region');
  const [regionsLoading, setRegionsLoading] = useState(false);
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const currentRegionName =
    regionContext?.name ||
    currentUser?.adminRegionName ||
    currentUser?.admin_region_name;
  const miniProgramName =
    regionContext?.miniProgramName || miniProgramContext?.name;

  useEffect(() => {
    let mounted = true;
    if (!switchPolicy.canSelectAnyRegion) {
      setRegions([]);
      return () => {
        mounted = false;
      };
    }

    setRegionsLoading(true);
    listEnabledRegionsForSwitch()
      .then((list) => {
        if (mounted) {
          setRegions(list);
        }
      })
      .catch(() => {
        if (mounted) {
          setRegions([]);
          message.error('区域列表加载失败，请稍后重试');
        }
      })
      .finally(() => {
        if (mounted) {
          setRegionsLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [regionContext?.miniProgramId, switchPolicy.canSelectAnyRegion]);

  const regionSwitchMode = resolveRegionSwitchMode(
    switchPolicy.canSelectAnyRegion,
    regions.length,
  );
  const displayRegionName =
    currentRegionName ||
    regions.find((item) => item.id === regionContext?.id)?.name;

  return (
    <Space size={8}>
      {miniProgramName ? <Button disabled>{miniProgramName}</Button> : null}
      {!switchPolicy.canSelectAnyRegion && currentRegionName ? (
        <Button disabled>{currentRegionName}</Button>
      ) : null}
      {switchPolicy.canSelectAnyRegion && regionsLoading ? (
        <Button loading>加载区域</Button>
      ) : null}
      {switchPolicy.canSelectAnyRegion &&
      !regionsLoading &&
      regionSwitchMode !== 'menu' &&
      displayRegionName ? (
        <Button disabled>{displayRegionName}</Button>
      ) : null}
      {regionSwitchMode === 'menu' && !regionsLoading ? (
        <Dropdown
          menu={{
            items: regions.map((region) => ({
              key: region.id,
              label: region.name,
              disabled: region.id === regionContext?.id,
              onClick: () => switchCurrentRegion(region),
            })),
          }}
        >
          <Button>{displayRegionName || '切换区域'}</Button>
        </Dropdown>
      ) : null}
      {switchPolicy.showCompanyExit ? (
        <Button
          icon={<SwapOutlined />}
          onClick={() => {
            setAdminRegionContext(undefined);
            setAdminView('company');
            history.push('/company/miniprogram/regions');
          }}
        >
          切换至公司管理
        </Button>
      ) : null}
      {switchPolicy.showPlatformExit ? (
        <Button
          icon={<SwapOutlined />}
          onClick={() => {
            setAdminMiniProgramContext(undefined);
            setAdminRegionContext(undefined);
            setAdminView('platform');
            history.push(platformHomePath);
          }}
        >
          切换至平台管理
        </Button>
      ) : null}
    </Space>
  );
};

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const platformAdmin = isPlatformAdmin(initialState?.currentUser);
  const availableViews = resolveAvailableAdminViews(initialState?.currentUser);
  const activeView: AdminView =
    initialState?.adminView && availableViews.includes(initialState.adminView)
      ? initialState.adminView
      : availableViews[0] || 'company';

  return {
    actionsRender: () => [
      activeView === 'company' && initialState?.currentUser ? (
        <MiniProgramContextSelector
          key="mini-program-context"
          platformAdmin={platformAdmin}
          currentUser={initialState.currentUser}
        />
      ) : null,
      activeView === 'region' && initialState?.currentUser ? (
        <RegionViewActions
          key="region-view-actions"
          currentUser={initialState.currentUser}
        />
      ) : null,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
      if (
        initialState?.adminView &&
        !availableViews.includes(initialState.adminView)
      ) {
        const fallbackView = availableViews[0] || 'company';
        setAdminView(fallbackView);
        setInitialState((state) => ({
          ...state,
          adminView: fallbackView,
        }));
      }
      const nextView = inferAdminViewFromPath(location.pathname);
      if (
        nextView &&
        availableViews.includes(nextView) &&
        nextView !== initialState?.adminView
      ) {
        setAdminView(nextView);
        setInitialState((state) => ({
          ...state,
          adminView: nextView,
        }));
      }
    },
    menuDataRender: (menuData) => filterMenuByView(menuData, activeView),
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  baseURL: '',
  ...errorConfig,
};
